import { RcFile } from 'antd/es/upload'
import axios, { CanceledError } from 'axios'
import { v4 } from 'uuid'

import { monitoring } from 'src/utils/monitoring'
import { configurePdfjsWorker } from 'src/utils/pdfjsWorker'

import {
  CreateFileMutationVariables,
  FileFragment,
  FileUploadCredentialsFragment,
  GetFileUploadCredentialsMutationVariables,
  ThumbnailFileFragment,
} from '../../types/graphql'
import { AcceptedAllFileTypes, MaxFileSize } from '../constants'
import { PathPrefixes } from '../constants/file'
import { FileUploadStatuses } from '../hooks/use-file-upload-state'

import { DateFormats, formatDate } from './date'

import { filterNil, formatBytes } from '.'

export enum MimeType {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  JPEG = 'image/jpeg',
  JPG = 'image/jpg', // nonstandard
  PNG = 'image/png',
}

export const MimeTypeDisplayNameMap: Record<MimeType, string> = {
  [MimeType.PDF]: 'PDF',
  [MimeType.DOCX]: 'DOCX',
  [MimeType.JPEG]: 'JPEG',
  [MimeType.JPG]: 'JPEG',
  [MimeType.PNG]: 'PNG',
}

export interface IUploader {
  abort: () => void
}
interface UploadFileParams {
  bucket: string
  createFile: (input: CreateFileMutationVariables) => Promise<ThumbnailFileFragment>
  file?: RcFile
  fileUrl?: string
  getFileUploadCredentials: (
    input: GetFileUploadCredentialsMutationVariables
  ) => Promise<FileUploadCredentialsFragment | null | undefined>
  onError?: (errorType: string) => void
  pathPrefix?: PathPrefixes
  setFileName: (name: string) => void
  setFileSize: (fileSize: number) => void
  setFileUploadProgress: (progress: number) => void
  setFileUploadStatus: (status: FileUploadStatuses) => void
  setGovWellFile?: (govWellFile: FileFragment) => void
  setPath: (path: string) => void
  setUploaderRef: (upload: IUploader) => void
  shouldAnnotateImageWithExifContext?: boolean
  shouldUploadFileToOpenAI?: boolean
}

export const formatFileProgress = (progress: number) => Math.min(99, Math.round(progress * 100))

export const isPdf = (fileType: string) => fileType === MimeType.PDF

export const isImage = (fileType: string) => {
  return ([MimeType.JPEG, MimeType.PNG, MimeType.JPG] as string[]).includes(fileType)
}

export const getFileName = (file: RcFile): string => {
  const typeWord = file?.type.startsWith('image/') ? 'Photo' : 'File'
  const now = formatDate(new Date(), DateFormats.NumericalDateTimeConcise)
  return file?.name ?? `${typeWord} uploaded at ${now}`
}

export enum FileUploadErrors {
  EncryptedPdf = 'EncryptedPdf',
  InvalidPdf = 'InvalidPdf',
  TooBig = 'TooBig',
  UnsupportedFileType = 'UnsupportedFileType',
  UploadFailed = 'UploadFailed',
}

export const uploadFile = async (
  params: UploadFileParams
): Promise<ThumbnailFileFragment | undefined> => {
  const {
    bucket,
    createFile,
    file,
    fileUrl,
    getFileUploadCredentials,
    onError,
    pathPrefix,
    setFileName,
    setFileSize,
    setFileUploadProgress,
    setFileUploadStatus,
    setGovWellFile,
    setPath,
    setUploaderRef,
    shouldAnnotateImageWithExifContext,
    shouldUploadFileToOpenAI,
  } = params

  // for files where it's a data url (e.g. signature) the name doesn't matter
  const fileName = file ? getFileName(file) : ''

  setFileName(fileName)
  if (file) {
    setFileSize(file.size)
  }
  setFileUploadStatus(FileUploadStatuses.Uploading)

  const getBlobAndMetadata = async (): Promise<{ blob: Blob; size: number; type: string }> => {
    if (file) {
      return {
        blob: new Blob([file], { type: file.type }),
        size: file.size,
        type: file.type,
      }
    }
    if (!fileUrl) {
      throw new Error('fileUrl missing')
    }
    const dataUrlResponse = await axios.get(fileUrl, {
      responseType: 'blob',
    })
    return {
      blob: dataUrlResponse.data,
      size: dataUrlResponse.data.size,
      type: dataUrlResponse.data.type,
    }
  }

  const handleProgress = (progressEvent: {
    loaded?: number
    total?: number
    progress?: number
  }) => {
    const calculatedProgress = progressEvent.total
      ? (progressEvent.loaded || 0) / progressEvent.total
      : 0
    setFileUploadProgress(progressEvent.progress || calculatedProgress)
  }

  const { blob, size, type } = await getBlobAndMetadata()
  const generatedUuid = v4()
  const path = pathPrefix ? `${pathPrefix}/${generatedUuid}` : generatedUuid

  const credentials = await getFileUploadCredentials({ input: { bucket, path } })
  try {
    if (!credentials) {
      throw new Error('File upload credentials not available')
    }
    const { S3Client } = await import('@aws-sdk/client-s3')
    const { Upload } = await import('@aws-sdk/lib-storage')
    const client = new S3Client({
      region: credentials.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    })
    const upload = new Upload({
      client,
      params: {
        Bucket: credentials.bucket,
        Key: credentials.key,
        ContentType: type,
        Body: blob,
      },
    })
    setUploaderRef(upload)
    upload.on('httpUploadProgress', handleProgress)
    await upload.done()
  } catch (e) {
    if (
      // axios
      e instanceof CanceledError ||
      // aws sdk
      (e !== null && typeof e === 'object' && 'name' in e && e.name === 'AbortError')
    ) {
      return // ignore, user cancelled the upload
    }
    monitoring.logError(e, { context: 'uploadFile:s3Upload' })
    if (e instanceof Error) {
      onError?.(e?.name)
    }
    setFileUploadStatus(FileUploadStatuses.Error)
    return
  }

  try {
    const response = await createFile({
      input: {
        size,
        type,
        name: fileName,
        bucket,
        path,
        shouldAnnotateImageWithExifContext,
        shouldUploadFileToOpenAI,
      },
    })

    setPath(path)
    setFileUploadStatus(FileUploadStatuses.Done)
    setGovWellFile?.(response)

    return response
  } catch (e) {
    monitoring.logError(e, { context: 'uploadFile:createFile' })
    if (e instanceof Error) {
      onError?.(e?.name ?? e?.message)
    }
    setFileUploadStatus(FileUploadStatuses.Error)
    return undefined
  }
}

export const getErrorName = (errorType: string): string => {
  switch (errorType) {
    case FileUploadErrors.EncryptedPdf: {
      return 'You may not upload a password-protected PDF.'
    }
    case FileUploadErrors.InvalidPdf: {
      return 'This PDF file appears to be corrupted. Please try a different file.'
    }
    case FileUploadErrors.TooBig: {
      return `Files have a max size of ${formatBytes(MaxFileSize)}.`
    }
    case FileUploadErrors.UnsupportedFileType: {
      return 'File type not supported.'
    }
    default: {
      return 'Something went wrong uploading your file. Please try again.'
    }
  }
}

export const deduplicateFileFragments = (files: FileFragment[]): FileFragment[] => {
  const fileIds = new Set<number>()
  return files.reduce((prev: FileFragment[], curr: FileFragment) => {
    if (fileIds.has(curr.id)) {
      return prev
    }
    fileIds.add(curr.id)
    return [...prev, curr]
  }, [])
}

export const getIsFileExtensionAccepted = (args: { accept?: string; fileName: string }) => {
  const { accept, fileName } = args
  if (!accept || accept === AcceptedAllFileTypes) {
    return true
  }
  const acceptedFileExtensions = accept?.split(', ')
  return (
    !acceptedFileExtensions?.length ||
    acceptedFileExtensions.some((ext) => fileName.toLowerCase().endsWith(ext))
  )
}

// Vite resolves the worker URL at build time — see `src/utils/pdfjsWorker.ts`.

/**
 * Validates a PDF file before upload. Returns a FileUploadErrors value if the
 * PDF should be rejected, or undefined if the upload should proceed.
 *
 * - Encrypted/password-protected PDFs are blocked (EncryptedPdf)
 * - Corrupt/malformed PDFs are blocked (InvalidPdf)
 * - Infrastructure failures (worker load failure, etc.) are logged but the
 *   upload is allowed to proceed — the PDF itself is likely fine, and
 *   server-side processing will catch genuinely broken files.
 */
export const validatePdf = async (file: RcFile): Promise<FileUploadErrors | undefined> => {
  if (!isPdf(file.type)) {
    return undefined
  }
  try {
    const arrayBuffer = await file.arrayBuffer()
    await configurePdfjsWorker()
    const { pdfjs } = await import('react-pdf')
    await pdfjs.getDocument(new Uint8Array(arrayBuffer)).promise
    return undefined
  } catch (e) {
    if (e instanceof Error && e.name === 'PasswordException') {
      return FileUploadErrors.EncryptedPdf
    }
    if (e instanceof Error && e.name === 'InvalidPDFException') {
      return FileUploadErrors.InvalidPdf
    }
    monitoring.logError(e, { context: 'validatePdf' })
    return undefined
  }
}

type FileField = {
  file?: {
    id: number
  } | null
  files?: ({ id: number } | null)[] | null
}
export const getFileIdsFromFields = (fields: FileField[] | null | undefined): number[] =>
  filterNil(
    fields?.flatMap((f) => {
      return f.file?.id || f.files?.map((file) => file?.id) || []
    }) ?? []
  )

/**
 * Convert a File (or Blob) to a data URL (base64 string).
 */
export const fileToDataUrl = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      // result will be a base64-encoded data URL like "data:image/png;base64,..."
      resolve(reader.result as string)
    }

    reader.onerror = (err) => {
      reject(err)
    }

    reader.readAsDataURL(file)
  })
}
